import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import styled, { css, keyframes } from 'styled-components';
import { Avatar } from '@mui/material';
import { ChatMessage } from '../../types/next';

// Two soft pulses, about a second: draws the eye to a message the reader hasn't seen yet
const flash = keyframes`
  0%, 50%, 100% { box-shadow: 0 0 0 0 rgba(49, 121, 255, 0); background-color: inherit; }
  25%, 75% { box-shadow: 0 0 0 3px rgba(49, 121, 255, 0.6); background-color: #eaf2ff; }
`;

const Wrapper = styled.div<{ isUser: boolean; flashing: boolean }>`
  background: #f5f5f5;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  margin: 1rem;
  padding: 1rem;
  /* All left-aligned; the other side is indented one step, own comments are in the primary tint */
  ${({ isUser }) =>
    isUser
      ? css`
          background: #eaf2ff;
        `
      : css`
          margin-left: 3.5rem;
        `}
  ${({ flashing }) =>
    flashing
      ? css`
          animation: ${flash} 1.2s ease-in-out;
        `
      : ''}
`;

const Header = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: row;
`;

const HeaderDate = styled.div`
  font-size: 70%;
`;

const HeaderName = styled.div`
  font-size: 80%;
  font-weight: 500;
  &:before {
    content: '-';
    margin: 0 8px;
  }
`;

const Content = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  .MuiAvatar-root {
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
    margin: 10px;
  }
`;

const MessageContent = styled.div`
  width: 100%;
  margin-top: 10px;
`;

interface Props {
  message: ChatMessage;
  isUser?: boolean;
  // Flash once when it first scrolls into view
  highlight?: boolean;
}

const ChatMessageView = ({ message, isUser, highlight }: Props) => {
  const fullname = [message.user.firstName, message.user.lastName].join(' ');
  const ref = useRef<HTMLDivElement>(null);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!highlight || !element || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setFlashing(true);
          observer.disconnect();
        }
      },
      { threshold: 0.6 }
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [highlight]);

  return (
    <Wrapper ref={ref} isUser={!!isUser} flashing={flashing}>
      <Header isUser={!!isUser}>
        <HeaderDate>{moment(message.creationDate).format('DD-MM-YYYY HH:mm')}</HeaderDate>
        <HeaderName>{fullname}</HeaderName>
      </Header>
      <Content isUser={!!isUser}>
        <Avatar src={message.user.avatarUrl}>AA</Avatar>
        <MessageContent>{message.content}</MessageContent>
      </Content>
    </Wrapper>
  );
};

export default ChatMessageView;
